import { getCallable } from '../firebase/config';

// Log levels
export const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
};

// Log categories
export const LOG_CATEGORIES = {
  AUTH: 'auth',
  ADMIN: 'admin',
  UPLOAD: 'upload',
  API: 'api',
  SECURITY: 'security',
  REACT: 'react',
  PERFORMANCE: 'performance',
  USER_ACTION: 'user_action',
};

/**
 * Firestore write policy (Wave 3):
 * - Ordinary USER_ACTION / PERFORMANCE / DEBUG → console only (never Firestore)
 * - AUTH success → heavily sampled; AUTH failure → always (via trusted CF)
 * - SECURITY / ADMIN / ERROR → always via trusted CF
 * - API → errors always; successes sampled
 */
const FIRESTORE_SAMPLE = {
  [LOG_CATEGORIES.USER_ACTION]: 0,
  [LOG_CATEGORIES.PERFORMANCE]: 0,
  [LOG_CATEGORIES.DEBUG]: 0,
  [LOG_CATEGORIES.API]: 0.02,
  [LOG_CATEGORIES.AUTH]: 0.05, // overridden for failures
  [LOG_CATEGORIES.REACT]: 0.05,
  [LOG_CATEGORIES.UPLOAD]: 0.1,
  [LOG_CATEGORIES.ADMIN]: 1,
  [LOG_CATEGORIES.SECURITY]: 1,
};

const ALWAYS_PERSIST_LEVELS = new Set([LOG_LEVELS.ERROR, LOG_LEVELS.WARN]);

class SystemLogger {
  constructor() {
    this.userId = null;
    this.sessionId = this.generateSessionId();
    this._logTrustedEvent = null;
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  setUser(userId) {
    this.userId = userId;
  }

  shouldPersist({ level, category, metadata = {} }) {
    if (ALWAYS_PERSIST_LEVELS.has(level)) return true;
    if (category === LOG_CATEGORIES.SECURITY) return true;
    if (category === LOG_CATEGORIES.ADMIN) return true;
    if (category === LOG_CATEGORIES.AUTH && metadata.success === false) return true;
    const rate = FIRESTORE_SAMPLE[category];
    if (rate == null || rate <= 0) return false;
    if (rate >= 1) return true;
    return Math.random() < rate;
  }

  async getTrustedLogger() {
    if (!this._logTrustedEvent) {
      // Wave 6 — wait for App Check before enforced callable
      this._logTrustedEvent = await getCallable('logTrustedEvent');
    }
    return this._logTrustedEvent;
  }

  async persistViaFunction(logData) {
    try {
      const fn = await this.getTrustedLogger();
      await fn({
        level: logData.level,
        category: logData.category,
        message: logData.message,
        metadata: logData.metadata,
        source: logData.source,
      });
      return true;
    } catch (error) {
      // Never fall back to open client writes — that was the spam vector.
      if (process.env.NODE_ENV === 'development') {
        console.warn('logTrustedEvent failed:', error?.message || error);
      }
      return false;
    }
  }

  async logEvent({
    level = LOG_LEVELS.INFO,
    category = LOG_CATEGORIES.API,
    message,
    metadata = {},
    source = 'unknown',
    userId = null,
  }) {
    const logData = {
      level,
      category,
      message,
      userId: userId || this.userId,
      sessionId: this.sessionId,
      metadata: {
        ...metadata,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        timestamp: new Date().toISOString(),
        timezone:
          typeof Intl !== 'undefined'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : '',
      },
      source,
    };

    const consoleMethod = this.getConsoleMethod(level);
    if (process.env.NODE_ENV === 'development') {
      if (category !== LOG_CATEGORIES.USER_ACTION || level === LOG_LEVELS.ERROR) {
        consoleMethod(`[${category.toUpperCase()}] ${message}`, metadata);
      }
    }

    if (!this.shouldPersist({ level, category, metadata })) {
      return false;
    }

    return this.persistViaFunction(logData);
  }

  getConsoleMethod(level) {
    switch (level) {
      case LOG_LEVELS.DEBUG:
        return console.debug;
      case LOG_LEVELS.INFO:
        return console.info;
      case LOG_LEVELS.WARN:
        return console.warn;
      case LOG_LEVELS.ERROR:
        return console.error;
      default:
        return console.log;
    }
  }

  debug(message, metadata = {}, source = 'app') {
    return this.logEvent({
      level: LOG_LEVELS.DEBUG,
      message,
      metadata,
      source,
    });
  }

  info(message, metadata = {}, source = 'app') {
    return this.logEvent({
      level: LOG_LEVELS.INFO,
      message,
      metadata,
      source,
    });
  }

  warn(message, metadata = {}, source = 'app') {
    return this.logEvent({
      level: LOG_LEVELS.WARN,
      message,
      metadata,
      source,
    });
  }

  error(message, metadata = {}, source = 'app') {
    return this.logEvent({
      level: LOG_LEVELS.ERROR,
      message,
      metadata,
      source,
    });
  }

  async logAuth(action, success = true, metadata = {}) {
    return this.logEvent({
      level: success ? LOG_LEVELS.INFO : LOG_LEVELS.WARN,
      category: LOG_CATEGORIES.AUTH,
      message: `Authentication ${action}: ${success ? 'Success' : 'Failed'}`,
      metadata: {
        action,
        success,
        ...metadata,
      },
      source: 'auth',
    });
  }

  async logUserAction(action, metadata = {}) {
    // Never persists — kept for API compat / local console in dev
    return this.logEvent({
      level: LOG_LEVELS.INFO,
      category: LOG_CATEGORIES.USER_ACTION,
      message: `User action: ${action}`,
      metadata: {
        action,
        ...metadata,
      },
      source: 'user',
    });
  }

  async logAdminAction(action, targetUserId = null, metadata = {}) {
    return this.logEvent({
      level: LOG_LEVELS.INFO,
      category: LOG_CATEGORIES.ADMIN,
      message: `Admin action: ${action}`,
      metadata: {
        action,
        targetUserId,
        ...metadata,
      },
      source: 'admin',
    });
  }

  async logSecurityEvent(eventType, severity = 'medium', metadata = {}) {
    return this.logEvent({
      level: LOG_LEVELS.WARN,
      category: LOG_CATEGORIES.SECURITY,
      message: `Security event: ${eventType}`,
      metadata: {
        eventType,
        severity,
        ...metadata,
      },
      source: 'security',
    });
  }

  async logPerformance(metric, value, metadata = {}) {
    return this.logEvent({
      level: LOG_LEVELS.INFO,
      category: LOG_CATEGORIES.PERFORMANCE,
      message: `Performance metric: ${metric} = ${value}`,
      metadata: {
        metric,
        value,
        ...metadata,
      },
      source: 'performance',
    });
  }

  async logApiCall(endpoint, method, status, duration, metadata = {}) {
    const level = status >= 400 ? LOG_LEVELS.ERROR : LOG_LEVELS.INFO;

    return this.logEvent({
      level,
      category: LOG_CATEGORIES.API,
      message: `API ${method} ${endpoint} - ${status} (${duration}ms)`,
      metadata: {
        endpoint,
        method,
        status,
        duration,
        ...metadata,
      },
      source: 'api',
    });
  }
}

const systemLogger = new SystemLogger();

export const logSystemEvent = systemLogger.logEvent.bind(systemLogger);
export const logAuth = systemLogger.logAuth.bind(systemLogger);
export const logUserAction = systemLogger.logUserAction.bind(systemLogger);
export const logAdminAction = systemLogger.logAdminAction.bind(systemLogger);
export const logSecurityEvent = systemLogger.logSecurityEvent.bind(systemLogger);
export const logPerformance = systemLogger.logPerformance.bind(systemLogger);
export const logApiCall = systemLogger.logApiCall.bind(systemLogger);

export default systemLogger;
